import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription, interval } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  AlertDialogComponent,
  AlertDialogData,
} from 'src/app/modules/shared/components/dialogs/alert-dialog/alert-dialog.component';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from 'src/app/modules/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { AuthService } from 'src/app/pages/core/abstraction/auth.service';
import { CryptoService } from 'src/app/pages/core/abstraction/crypto.service';
import { IRule, IRuleStatsDto, STATUS } from 'src/app/pages/core/models/Dca';
import { ErrorHandlingService } from 'src/app/pages/core/services/utils/error-handling.service';

@Component({
  selector: 'app-rule-card',
  templateUrl: './rule-card.component.html',
  styleUrls: ['./rule-card.component.scss'],
})
export class RuleCardComponent implements OnInit, OnDestroy {
  @Input() rule: IRule;
  @Input() isMobile: boolean;
  @Output() updateRules: EventEmitter<boolean> = new EventEmitter();
  next = {
    nextNumberDays: null,
    nextHours: null,
    equalDay: null,
  };
  openCollapse: boolean = false;
  ruleStats: Observable<IRuleStatsDto>;
  loadingChangeState: boolean;
  openCollapseMobile: boolean = false;
  tabMobile: string = 'Detail';
  checkIsStable: boolean;
  intervalSubscription: Subscription;
  STATUS = STATUS;
  Math = Math;
  constructor(
    private authService: AuthService,
    private matDialog: MatDialog,
    private errorHandlingService: ErrorHandlingService,
    public cryptoService: CryptoService,
  ) {}

  ngOnInit(): void {
    this.next.nextNumberDays = this.calculateNextDays();
    this.checkCoinStable();
    this.listenToAutomaticUpdate();
    if (this.rule?.statusCollapse) {
      if (this.isMobile) this.openCollapseMobile = true;
      else {
        this.openCollapse = true;
        this.ruleStats = this.authService.getStatsRule(this.rule.id);
      }
    } else {
      this.openCollapse = false;
      this.openCollapseMobile = false;
    }
  }

  ngOnDestroy() {
    if (this.intervalSubscription) {
      this.intervalSubscription.unsubscribe();
    }
  }

  calculateNextDays(): number {
    let nextTimeFormat = new Date(this.rule.nextRunTime);
    const currentDay = new Date();
    this.next.nextHours = nextTimeFormat.getHours();
    this.next.equalDay = currentDay.getDay() === nextTimeFormat.getDay();
    let differ = Math.floor(
      (nextTimeFormat.getTime() - currentDay.getTime()) / (1000 * 60 * 60 * 24),
    );
    return differ;
  }

  changeState() {
    this.loadingChangeState = true;
    let statusCollapse;
    this.isMobile
      ? (statusCollapse = this.openCollapseMobile)
      : (statusCollapse = this.openCollapse);
    if (this.rule.status === STATUS.ACTIVE) {
      this.authService.changeStatusRule(this.rule.id, 'PAUSED', statusCollapse).subscribe(
        () => (this.loadingChangeState = false),
        (error) => {
          console.log('Error ', error);
          this.loadingChangeState = false;
          this.matDialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
            panelClass: 'modal-container',
            data: this.errorHandlingService.handleDcaRulesError(error),
          });
        },
      );
    } else {
      this.authService.changeStatusRule(this.rule.id, 'ACTIVE', statusCollapse).subscribe(
        () => (this.loadingChangeState = false),
        (error) => {
          console.log('Error ', error);
          this.loadingChangeState = false;
          this.matDialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
            panelClass: 'modal-container',
            data: this.errorHandlingService.handleDcaRulesError(error),
          });
        },
      );
    }
  }

  deleteRule() {
    this.matDialog
      .open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
        panelClass: 'modal-container',
        data: {
          title: '¿Deseas eliminar la regla?',
          content: 'Haz click en aceptar para confirmar.',
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.authService.deleteRule(this.rule.id).subscribe(
            () => {
              this.matDialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
                panelClass: 'modal-container',
                data: {
                  title: '¡Regla eliminada!',
                  content: 'Se ha eliminado correctamente la regla.',
                },
              });
            },
            (error) => {
              console.log('Error ', error);
              this.matDialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
                panelClass: 'modal-container',
                data: this.errorHandlingService.handleDcaRulesError(error),
              });
            },
          );
        }
      });
  }

  getStats() {
    this.openCollapse = !this.openCollapse;
    if (this.openCollapse) this.ruleStats = this.authService.getStatsRule(this.rule.id);
  }

  changeTab(tab: string) {
    this.tabMobile = tab;
    if (tab === 'Stats') this.ruleStats = this.authService.getStatsRule(this.rule.id);
  }

  checkCoinStable() {
    if (this.rule.fromTicker !== 'ARS' && this.rule.fromTicker !== 'USD')
      this.checkIsStable = this.cryptoService.isStableCoin(this.rule.fromTicker);
    else this.checkIsStable = true;
  }

  listenToAutomaticUpdate() {
    if (this.next.nextNumberDays === 0) {
      const nextRunTime = new Date(this.rule.nextRunTime);
      this.intervalSubscription = interval(1000)
        .pipe(filter(() => new Date() >= nextRunTime && this.next.nextNumberDays === 0))
        .subscribe(() => {
          this.updateRules.emit(true);
        });
    }
  }
}
